import { Box, Button, Grid, Skeleton, Theme, Typography } from "@mui/material";
import MarketCapAllocation from "./marketCapAllocation";
import AmcPerformanceAnalysis from "./amcPerformanceAnalysis";
import { AssetGroupDataProps, InfoCardProps, InfoCardsData } from "../types";
import { AlphaIcon, BetaIcon, ChartIcon, ProductsGainLossIcon } from "../../../../../assets/SvgIcons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTotalValues } from "../../../../../redux-store/actions/analytics";
import { RootStateType } from "../../../../../redux-store/reducers";
import { GetTotalValuesResponseBody } from "../../../../../redux-store/types/analytics";
import SchemewiseAnalysis from "./schemewiseAnalysis";

const initialProductsInfoCardsDataArray = [
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <ProductsGainLossIcon {...props} viewBox="0 0 57 40" />,
        title: "Gain/Loss (%)",
        value: 0,
        valueTextColor: 'success.800'
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 40" />,
        title: "Absolute Return",
        value: 0
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <AlphaIcon {...props} viewBox="0 0 57 40" />,
        title: "Alpha",
        value: 0,
        valueTextColor: 'success.800'
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <BetaIcon {...props} viewBox="0 0 57 40" />,
        title: "Beta",
        // value: 0,
        value: 0.98,
        valueTextColor: 'warning.500'
    }
]

const tabs = [
    "Mutual Fund",
    "Direct Equity",
    "PMS",
    "Bonds",
    "Alternates"
]


function InfoCard({
    Icon,
    title,
    value = 0,
    percentage = 0,
    valueTextColor = "text.primary",
    isLoading = false,
}: InfoCardProps) {
    const formatedPercentage = !Number.isNaN(Number(percentage ?? 0)) ?
        Math.round((percentage ?? 0) * 100) / 100 : '0'

    return (
        <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                borderRadius: "15px",
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                padding: "25px 18px",
                display: "flex",
                height: "100%",
                alignItems: "center",
            })}
        >
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Icon />
            </Box>
            <Box
                sx={{
                    flex: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    rowGap: "8px",
                    width: '80%',
                }}
            >
                {isLoading && <>
                    <Skeleton
                        animation="wave"
                        sx={{ width: '60%' }}
                    />
                    <Skeleton
                        animation="wave"
                        sx={{ width: '100%' }}
                    />
                </>}
                {!isLoading && (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "14px",
                                color: theme.palette.text.primary,
                                fontWeight: 500
                            })}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "27.5px",
                                color: valueTextColor
                            }}
                        >
                            {title === "Gain/Loss (%)" && (
                                !Number.isNaN(Number(value ?? 0)) ?
                                    `${Math.round((value ?? 0) * 100) / 100} %` : '0 %'
                            )}
                            {title === "Absolute Return" && (
                                !Number.isNaN(Number(value ?? 0)) ?
                                    `${Math.round((value ?? 0) * 100) / 100} %` : '0 %'
                            )}
                            {title === "Alpha" && (
                                !Number.isNaN(Number(value ?? 0)) ?
                                    `${Math.round((value ?? 0) * 100) / 100}` : '0'
                            )}
                            {title === "Beta" && (
                                // !Number.isNaN(Number(value ?? 0)) ?
                                //     `${Math.round((value ?? 0) * 100) / 100}` : '0'
                                0.98
                            )}
                        </Typography>

                    </>
                )}
            </Box>

        </Box>
    )
}


export default function AssetGroupData({
    clientCode,
    portfolioId
}: AssetGroupDataProps): JSX.Element {
    const dispatch = useDispatch();

    const [productInfoCardsDataLoading, setProductInfoCardsDataLoading] = useState<boolean>(false)
    const [productInfoCardsDataObj, setProductInfoCardsDataObj] = useState<{ isSuccess: boolean; data: InfoCardsData[] }>({ isSuccess: true, data: initialProductsInfoCardsDataArray })

    const [activeTab, setActiveTab] = useState<string>("Mutual Fund")

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchProductInfoCardsData = async (
        clientCode: string,
        portfolioId: string,
        assetGroup: string
    ) => {
        try {
            setProductInfoCardsDataLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId,
                assetGroup: assetGroup === "Mutual Fund" ? "MF" : null,
            }

            const response = await (dispatch(getTotalValues(payload)) as unknown as Promise<GetTotalValuesResponseBody>);

            setProductInfoCardsDataObj(prevState => ({
                ...prevState,
                isSuccess: response?.isSuccess ?? true,
                message: response?.message ?? "",
                data: prevState.data.map(obj => ({
                    ...obj,
                    ...((obj?.title === "Gain/Loss (%)") ? {
                        value: response?.result?.percentage ?? 0
                    } : {}),
                    ...((obj?.title === "Absolute Return") ? {
                        value: response?.result?.irr ?? 0
                    } : {}),
                    ...((obj?.title === "Alpha") ? {
                        value: response?.result?.alpha ?? 0,
                    } : {}),
                    ...((obj?.title === "Beta") ? {
                        value: response?.result?.beta ?? 0,
                    } : {}),
                }))
            }))
        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setProductInfoCardsDataLoading(false)
        }
    }

    useEffect(() => {
        fetchProductInfoCardsData(
            clientCode,
            portfolioId,
            activeTab
        )
    }, [clientCode, portfolioId])

    const handleTabChange = (selectedTab: string) => {
        setActiveTab(selectedTab)

        fetchProductInfoCardsData(
            clientCode,
            portfolioId,
            selectedTab
        )
    }


    return (
        <>
            {/* Mutual Fund , Direct Equity, PMS, Bonds, Alternates tab */}
            <Box
                sx={(theme: Theme) => ({
                    display: 'flex',
                    gap: '10px',
                    paddingBottom: '15px',
                    borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                    overflowX: 'auto',
                    marginTop: "65px"
                })}
            >
                {tabs.map((item) => {
                    return (
                        <Button
                            variant={activeTab === item ? "contained" : "text"}
                            key={item}
                            onClick={() => handleTabChange(item)}
                            sx={(theme: Theme) => ({
                                padding: '0px 15px',
                                background: activeTab === item ? theme.palette.primary[500] : 'transparent',
                                borderRadius: '7px',
                                outline: 'none',
                                border: 'none',
                                cursor: 'pointer'
                            })}
                            disabled={item !== "Mutual Fund"}
                        >
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: activeTab === item ? theme.palette.textDark.primary : theme.palette.text.secondary
                                })}
                            >
                                {item}
                            </Typography>
                        </Button>
                    )
                })}
            </Box>

            {/* Market Cap Allocation */}
            <Box
                sx={(theme: Theme) => ({
                    backgroundColor: theme.palette.common.white,
                    borderRadius: "20px",
                    padding: "30px",
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginTop: "36px"
                })}
            >
                <MarketCapAllocation
                    key={activeTab}
                    assetGroup={activeTab}
                    clientCode={clientCode}
                    portfolioId={portfolioId}
                />
            </Box>

            {/* AMC Performance Analysis */}
            <Box
                sx={(theme: Theme) => ({
                    backgroundColor: theme.palette.common.white,
                    borderRadius: "20px",
                    padding: "30px",
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginTop: "36px"
                })}
            >
                <AmcPerformanceAnalysis
                    key={activeTab}
                    assetGroup={activeTab}
                    clientCode={clientCode}
                    portfolioId={portfolioId}
                />
            </Box>

            {/* Gain/Loss, XIRR, Alpha, Beta info cards */}

            <Grid
                container
                spacing={2}
                sx={{ marginTop: '32px' }}
            >
                {productInfoCardsDataObj.data.map((obj: InfoCardsData) => (
                    <Grid
                        item
                        key={obj.title}
                        xs={12}
                        md={6}
                        lg={3}
                    >
                        <InfoCard
                            Icon={obj?.Icon}
                            title={obj?.title}
                            value={obj?.value}
                            percentage={obj?.percentage}
                            valueTextColor={obj?.valueTextColor}
                            isLoading={productInfoCardsDataLoading}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Schemewise Analysis */}
            <Box
                sx={(theme: Theme) => ({
                    backgroundColor: theme.palette.common.white,
                    borderRadius: "20px",
                    padding: "30px",
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    marginTop: "36px"
                })}
            >
                <SchemewiseAnalysis
                    key={activeTab}
                    clientCode={clientCode}
                    portfolioId={portfolioId}
                    assetGroup={activeTab}
                />
            </Box>

        </>
    )
}